import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { toJS } from 'mobx';

import ContentContainer from '../../components/ContentContainer';
import PageTitle from '../../components/PageTitle';
import PasswordForm from '../../components/PasswordForm';
import withUser from '../../hoc/withUser';

import LanguageForm from './components/LanguageForm';
import './styles.scss';
import DeleteAccount from '../../components/DeleteAccount';
import DeleteConfirmModal from '../../components/Modal/DeleteConfirmModal';

import DeleteModal from '../../components/Modal/DeleteModal';

//HELPER
import { checkIsProfessionalUser } from './helper/AccountSettings';
import SelectSuccessorModal from '../../components/Modal/SelectSuccessorModal';
import { getDescriptionDeleteAccountSuccessorModal } from '../../utils/helpers';
import DropdownMenu from '../../components/DropdownMenu';
import { successAlert } from '../../utils/alerts';
import { useStore } from '../../store';

const AccountSettings = observer(({ user, authToken, logout }) => {
  const {
    rootStore: {
      profileStore,
      profileStore: { contactStore },
    },
  } = useStore();

  const [deleteModal, setDeleteModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [isSuccessorSelected, setIsSuccessorSelected] = useState(false);
  const [selectedSuccessorId, setSelectedSuccessorId] = useState(null);
  const [selectedSuccessorName, setSelectedSuccessorName] = useState('');
  const [primaryButtonDisabled, setPrimaryButtonDisabled] = useState(false);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!profileStore.profileData) {
      profileStore.initProfile(user, authToken, i18n);
    }
    // eslint-disable-next-line
  }, []);

  const confirmDeleteAccount = async () => {
    setPrimaryButtonDisabled(true);

    const isProfessionalUser = checkIsProfessionalUser(toJS(profileStore.profileData.user_type));
    // END-USERS
    if (!isProfessionalUser) {
      setDeleteConfirmationModal(true);
      return;
    }

    // PROFESSIONAL-USERS
    const { isLastCompanyAdmin } = await contactStore.getUsersListIfAdminIsLastCompanyAdmin();

    if (isLastCompanyAdmin) {
      setSuccessModal(true);
      setDeleteModal(false);
    } else {
      setDeleteModal(false);
      setDeleteConfirmationModal(true);
    }
  };

  const handleChangeLanguage = async values => {
    await profileStore.saveProfile(values, t);
  };

  const handleSuccessorUserForCompanyAdmin = (userId, successorName) => {
    setIsSuccessorSelected(true);
    setSelectedSuccessorId(userId);
    setSelectedSuccessorName(successorName);
  };

  const handleConfirmSuccessor = async () => {
    const response = await contactStore.assignUserAsCompanyAdmin(selectedSuccessorId);

    if (response) {
      setDeleteConfirmationModal(true);
      successAlert(
        t('contact.newCompanyAdminUpdateSuccess')
          .replace('%%SUCCESSOR%%', selectedSuccessorName)
          .replace('%%TARGET_COMPANY%%', profileStore.profileData.company.name)
      );
      setSuccessModal(false);
    }
  };

  const handleCancelSuccessorModal = () => {
    setSuccessModal(false);
    setPrimaryButtonDisabled(false);
  };

  const handleCloseDeleteInformationModal = () => {
    setDeleteModal(false);
    setPrimaryButtonDisabled(false);
  };

  const handleDeleteConfirmationModal = () => {
    profileStore.deleteStore.clearGlobalError();
    setDeleteConfirmationModal(false);
    setPrimaryButtonDisabled(false);
    setDeleteModal(false);
  };

  return (
    <>
      <ContentContainer>
        <PageTitle titleKey="passwordPage.title" subtitleKey="passwordPage.subtitle" />
        <LanguageForm
          formTitle={t('language.label')}
          onSubmit={handleChangeLanguage}
          initialValues={toJS(profileStore.profileData)}
        />
        <div className="form-wrappers">
          <h2>{t('deleteConfirmModal.passwordLabel')}</h2>
          <PasswordForm />
        </div>
        <DeleteAccount toggleDeleteModal={() => setDeleteModal(true)} disabled={false} />

        <DeleteModal
          isOpen={deleteModal}
          onCloseModal={handleCloseDeleteInformationModal}
          toggleConfirm={confirmDeleteAccount}
          primaryButtonDisabled={primaryButtonDisabled}
        />
        <SelectSuccessorModal
          isOpen={successModal}
          modalTitle={t('selectSuccessorModal.modalTitle')}
          title={t('selectSuccessorModal.title')}
          description={getDescriptionDeleteAccountSuccessorModal(
            t('selectSuccessorModal.description'),
            profileStore.company?.name
          )}
          onClose={handleCancelSuccessorModal}
          onConfirm={handleConfirmSuccessor}
          dropdownDescription={t('selectSuccessorModal.dropdownDescription')}
          primaryButtonTitle={t('deleteConfirmModal.deleteButton')}
          isItemSelected={isSuccessorSelected}
          validationMessage={t('selectSuccessorModal.switchCompany.validationMessage')}
        >
          <DropdownMenu
            options={toJS(contactStore.listProfessionalUsers)}
            onSelect={handleSuccessorUserForCompanyAdmin}
          />
        </SelectSuccessorModal>

        <DeleteConfirmModal isOpen={deleteConfirmationModal} toggle={handleDeleteConfirmationModal} logout={logout} />
      </ContentContainer>
    </>
  );
});

export default withUser(AccountSettings);
